import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import "regenerator-runtime/runtime";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import Icon from "../../YourAIAgents/components/components/icon";
import { Tooltip } from "@mui/material";
import CommonModal from "../../Settings/components/ResetContext/components/DumpModal";
import { useNavigate, useParams } from "react-router-dom";
import Uploadfile from "./uploadFiles/index";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import toast from "react-hot-toast";
import { MicIcon, SendArrowIcon } from "./WelcomeSections/InputIcons";
// shadcn/radix tooltip for the composer controls — the MUI Tooltip above is
// still used for the over-limit warning on a file thumbnail.
import {
  Tooltip as HoverTip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";

const fileDedupKey = (f) => {
  const base = f?._localFile && typeof f._localFile === "object" ? f._localFile : f;
  return `${base?.name || f?.name || f?.filename || ""}|${base?.size ?? f?.size ?? ""}|${base?.lastModified ?? f?.lastModified ?? ""}`;
};

const notifyDuplicateUpload = () => {
  toast.error(
    <span>
      <b>Duplicate upload</b>
      <br />
      <span style={{ fontSize: "12px" }}>This file/image has already been attached.</span>
    </span>,
  );
};

const dedupeAgainstSelected = (incoming, existingList) => {
  const seen = new Set(existingList.map(fileDedupKey));
  const fresh = [];
  let duplicates = 0;
  for (const file of incoming) {
    const key = fileDedupKey(file);
    if (seen.has(key)) {
      duplicates += 1;
      continue;
    }
    seen.add(key);
    fresh.push(file);
  }
  return { fresh, duplicates };
};

const ALLOWED_FILE_TYPES = [
  // Images
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/vnd.microsoft.icon",
  // Text & docs
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Audio / video
  "audio/mpeg",
  "audio/wav",
  "audio/mp4",
  "audio/x-m4a",
  "audio/flac",
  "video/mp4",
  "video/mpeg",
  "video/webm",
  "application/json",
  "text/json",
];

const isAllowedFile = (file) => {
  if (!file) return false;
  if (ALLOWED_FILE_TYPES.includes(file.type)) return true;
  const name = (file.name || "").toLowerCase();
  return /\.(jpe?g|png|gif|webp|json)$/.test(name);
};

const showUnsupportedFileError = (rejected) => {
  if (!rejected || rejected.length === 0) return;
  const container = document.getElementById("errorofmaxfile");
  if (!container) return;
  const div = document.createElement("div");
  div.style.display = "flex";
  div.style.justifyContent = "start";
  div.style.alignItems = "center";
  div.style.gap = "10px";
  div.style.padding = "0px 22px";
  div.className = "border !border-[#f6dba6] !bg-[#fef7e1] rounded-xl";
  div.innerHTML = `
    <span class="text-[#e69d17] text-[22px]">&#x26A0;</span>
    <p style="padding: 15px 0px; margin: 0px; color: black; text-align: left;">
      Unsupported file type: ${rejected.map((f) => f.name).join(", ")}
    </p>
  `;
  container.appendChild(div);
  setTimeout(() => div.remove(), 5000);
};

const filterAllowedFiles = (incoming) => {
  const list = Array.from(incoming || []);
  const allowed = list.filter(isAllowedFile);
  const rejected = list.filter((f) => !isAllowedFile(f));
  showUnsupportedFileError(rejected);
  return allowed;
};

const ChatInput = ({
  submitNewMessage,
  setSelectedFiles,
  selectedFiles,
  prefillMessage,
  onPrefillConsumed,
}) => {
  const [micStatus, setMicStatus] = useState("unknown"); // 👉 "unknown" | "granted" | "denied"
  const textareaRef = useRef(null);
  const params = useParams();
  const [OpenErrorModal, setOpenErrorModal] = useState(false);
  const navigate = useNavigate();
  const { selectedAssistant, isUserChating, contextuploadFilefrominput } = useUsersContext();
  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const [newMessage, setNewMessage] = useState("");
  const [uploadingKeys, setUploadingKeys] = useState(() => new Set());
  const [filesPopupOpen, setFilesPopupOpen] = useState(false);

  const addFilesWithUpload = async (incoming) => {
    const incomingList = Array.from(incoming || []);
    const fileItems = incomingList.filter((f) => f instanceof File);
    const nonFileItems = incomingList.filter((f) => !(f instanceof File));
    const allowedFiles = filterAllowedFiles(fileItems);
    const allItems = [...allowedFiles, ...nonFileItems];
    if (allItems.length === 0) return;

    const { fresh, duplicates } = dedupeAgainstSelected(allItems, selectedFiles);
    if (duplicates > 0) notifyDuplicateUpload();
    if (fresh.length === 0) return;

    let addedFiles = fresh;
    setSelectedFiles((prev) => [...prev, ...fresh]);

    const filesToUpload = addedFiles.filter((f) => f instanceof File);
    if (filesToUpload.length === 0) return;

    const uploadKeys = filesToUpload.map(fileDedupKey);
    setUploadingKeys((prev) => {
      const next = new Set(prev);
      uploadKeys.forEach((k) => next.add(k));
      return next;
    });

    try {
      const response = await contextuploadFilefrominput(
        filesToUpload,
        params?.id,
        () => { },
      );
      if (response?.data?.success === true && response?.data?.data) {
        const uploaded = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
        let idx = 0;
        setSelectedFiles((prev) =>
          prev.map((f) => {
            if (f instanceof File && filesToUpload.includes(f)) {
              const serverData = uploaded[idx++];
              if (!serverData) return f;
              return {
                ...serverData,
                _localFile: f,
                name: serverData.name || f.name,
                type: serverData.contentType || serverData.type || f.type,
              };
            }
            return f;
          }),
        );
      } else {
        toast.error(response?.message || "Something went wrong while uploading.");
      }
    } catch (err) {
      console.error("file upload failed:", err);
      toast.error("Something went wrong while uploading.");
    } finally {
      setUploadingKeys((prev) => {
        const next = new Set(prev);
        uploadKeys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };
  useEffect(() => {
    if (prefillMessage) {
      setNewMessage(prefillMessage);
      onPrefillConsumed?.();
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [prefillMessage, onPrefillConsumed]);

  // CU-86cb3trx4: the composer is the page's one job — put the cursor in it
  // on mount so typing works immediately. Delayed a tick so route transitions
  // and the sidebar don't steal focus back.
  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const [recordingLoading, setRecordingLoading] = useState(false);
  const processedLen = useRef(0);
  const micStreamRef = useRef(null);
  const [isOpenAddPromptModal, setIsAddPromptModal] = useState(false);
  // const { promptsData, getPrompts, hideloader } = useContext(PromptsContext);
  const [placeholdervalue, setplaceholdervalue] = useState(
    selectedAssistant?.assistant_name
      ? `How can ${selectedAssistant?.assistant_name} help you today?`
      : `How can we help ${userActiveLocation?.business_name} today?`,
  );

  useEffect(() => {
    setplaceholdervalue(
      selectedAssistant?.assistant_name
        ? `How can ${selectedAssistant?.assistant_name} help you today?`
        : `How can we help ${userActiveLocation?.business_name} today?`,
    );
  }, [selectedAssistant]);

  const autoResize = (el) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  useEffect(() => {
    autoResize(textareaRef.current);
  }, [newMessage]);

  // const validateAgentSelection = () => {
  //   if (!asistantSelected && !openAgentBuilder && !isAuto) {
  //     // showToast("Please select an agent before chat", "error");
  //     setTimeout(() => {
  //       window.parent.postMessage({ action: "redirect-ai-agents" }, "*");
  //     }, 1000);
  //     return false;
  //   }
  //   return true;
  // };

  const detectEnterPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (isUserChating) return;
      if (selectedFiles.length > 10) {
        const container = document.getElementById("errorofmaxfile");
        let div = document.createElement("div");
        div.style.display = "flex";
        div.style.justifyContent = "start";
        div.style.alignItems = "center";
        div.style.gap = "10px";
        div.style.padding = "0px 22px";
        div.className = "border !border-[#f6dba6] !bg-[#fef7e1] rounded-xl";
        div.innerHTML = `
        <span class="text-[#e69d17] text-[22px]">&#x26A0;</span>
        <p style="padding: 15px 0px; margin: 0px; color: black; text-align: left;">
            You can only upload 10 files at a time
        </p>
    `;
        container.appendChild(div);

        // Automatically remove the div after 5 seconds
        setTimeout(() => {
          div.remove();
        }, 5000);

        return true;
      }
      e.preventDefault();
      if (newMessage.trim()) {
        if (
          placeholdervalue === selectedAssistant?.assistant_name
            ? `How can ${selectedAssistant?.assistant_name} help you today?`
            : `How can we help ${userActiveLocation?.business_name} today?`
        ) {
          submitNewMessage(newMessage, "savetodb", "notimage", setNewMessage);
        } else {
          submitNewMessage(newMessage, "savetodb", "image", setNewMessage);
        }
      }
    }
  };

  const justCloseIt = () => {
    setIsAddPromptModal(false);
  };

  const handleFileRemove = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const [isDragging, setIsDragging] = useState(false);
  // CU-86cb3tnnu: the card border turns task-accent while the caret is in the box.
  const [isFocused, setIsFocused] = useState(false);
  const dragCounterRef = useRef(0);
  const uploadFileRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes("Files")) {
      dragCounterRef.current += 1;
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    // Routed through the same 32MB gate as the native "Add files and
    // photos" picker, so a dropped oversized file opens FileTooBigModal
    // too instead of silently going straight to addFilesWithUpload.
    uploadFileRef.current?.handleIncomingFiles(e.dataTransfer?.files);
  };

  const filePreviews = useMemo(() => {
    return selectedFiles.map((file) => {
      const localBlob =
        file instanceof Blob
          ? file
          : file?.file instanceof Blob
            ? file.file
            : file?._localFile instanceof Blob
              ? file._localFile
              : null;
      const name = file?.name || file?.filename || localBlob?.name || "";
      const mime =
        file?.type ||
        file?.contentType ||
        file?.mimeType ||
        localBlob?.type ||
        "";
      const remoteUrl = file?.url || file?.fileUrl || file?.src || null;
      const isImage =
        mime.startsWith?.("image/") ||
        /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);

      let previewUrl = null;
      let isObjectUrl = false;
      if (isImage) {
        if (localBlob) {
          previewUrl = URL.createObjectURL(localBlob);
          isObjectUrl = true;
        } else if (remoteUrl) {
          previewUrl = remoteUrl;
        }
      }

      return { name, isImage, previewUrl, isObjectUrl };
    });
  }, [selectedFiles]);

  useEffect(() => {
    return () => {
      filePreviews.forEach((p) => {
        if (p.isObjectUrl && p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
    };
  }, [filePreviews]);

  useEffect(() => {
    if (!listening) return;
    if (transcript.length > processedLen.current) {
      const diff = transcript.slice(processedLen.current);
      setNewMessage((prev) => prev + diff);
      processedLen.current = transcript.length;
    }
  }, [transcript, listening, setNewMessage]);

  const startListening = async () => {
    if (!browserSupportsSpeechRecognition) {
      setOpenErrorModal(true);
      return;
    }
    if (micStatus === "unknown") {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        micStreamRef.current = stream; // Save the stream reference
        setMicStatus("granted");
      } catch (err) {
        setMicStatus("denied");
        return;
      }
    }
    if (micStatus === "denied") {
      window.parent.postMessage({ type: "SHOW_MICROPHONE_ERROR" }, "*");
      return;
    }
    setRecordingLoading(true);
    SpeechRecognition.startListening({
      continuous: true,
      language: "en-US",
    });
  };

  const handleChange = (e) => {
    setNewMessage(e.target.value);

    if (e.target.value === "") {
      processedLen.current = 0;
      resetTranscript();
    }
  };

  const stopListening = () => {
    SpeechRecognition.stopListening();
    setRecordingLoading(false);
    resetTranscript();
    processedLen.current = 0;

    // Stop the original mic stream if present
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
      setMicStatus("unknown");
    }
  };

  return (
    <div>

      {micStatus === "denied" && <p className="!mb-2">
        You blocked microphone access. Please enable it in the browser’s site-
        permissions panel and reload the page.
      </p>}
      {!browserSupportsSpeechRecognition && <p className="!mb-2">Your browser doesn’t support speech recognition.</p>}
      <div className="chat__input-wrapper !mb-3">
        {/* ... other components ... */}

        {/* <AddPromptModal
        isModalOpen={isOpenAddPromptModal}
        categories={promptsData?.prompt_category?.filter(
          (obj) => obj.type !== "super-admin"
        )}
        subCategories={promptsData?.prompt_sub_category?.filter(
          (obj) => obj.type !== "super-admin"
        )}
        justCloseIt={justCloseIt}
        getUpdatePromptsData={getPrompts}
      /> */}


        {/* CU-123yxuah4fu: composer chrome matched to the Lovable
            DelegationPanel. A floating editorial surface (rounded-3xl card,
            soft drop shadow, hairline ring) with an ambient accent glow that
            fades in on focus. */}
        <div className="group relative w-full flex-1 min-w-0">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-4 rounded-[40px] bg-gradient-to-br from-[hsl(var(--task-accent)/0.08)] via-transparent to-[hsl(var(--task-accent)/0.05)] blur-3xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-700"
          />
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`relative w-full h-fit rounded-3xl bg-card transition-[box-shadow,border-color] duration-200 shadow-[0_20px_50px_hsl(var(--foreground)/0.04)] ring-1 ring-foreground/5 focus-within:shadow-[0_20px_50px_hsl(var(--task-accent)/0.10)] focus-within:ring-2 focus-within:ring-[hsl(var(--task-accent)/0.25)] ${isFocused
              ? "shadow-[0_20px_50px_hsl(var(--task-accent)/0.10)] ring-2 ring-[hsl(var(--task-accent)/0.25)]"
              : ""
              } ${isDragging ? "ring-2 ring-[hsl(var(--task-accent)/0.45)]" : ""}`}
          >
            {isDragging && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "24px",
                  background: "hsl(var(--card) / 0.85)",
                  backdropFilter: "blur(4px)",
                  WebkitBackdropFilter: "blur(4px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 50,
                  pointerEvents: "none",
                }}
              >
                <p className="m-0 text-[20px] font-medium text-foreground">
                  + Drop files here to add to chat
                </p>
              </div>
            )}
            {selectedFiles?.length > 0 && (
              <div className="flex flex-wrap gap-2 px-6 pt-5 pb-1">
                {filePreviews.map((preview, index) => {
                  const fileName = (preview.name || "").split("/").pop();
                  const shortName =
                    fileName.length > 14 ? `${fileName.slice(0, 12)}...` : fileName;
                  const isOverLimit = index >= 10;
                  const rawFile = selectedFiles[index];
                  const isUploading =
                    rawFile instanceof File &&
                    uploadingKeys.has(fileDedupKey(rawFile));
                  return (
                    <div
                      key={`${preview.name}-${index}`}
                      className="relative w-[96px] h-[96px]"
                      title={fileName}
                    >
                      <div className="w-full h-full  overflow-hidden border !border-border bg-card shadow-sm">
                        {preview.isImage && preview.previewUrl ? (
                          <img
                            src={preview.previewUrl}
                            alt={fileName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-1 bg-muted">
                            <Icon id="filePdf" />
                            <p className="text-[10px] m-0 mt-1 text-center text-muted-foreground leading-tight">
                              {shortName}
                            </p>
                          </div>
                        )}
                      </div>
                      {isUploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-card/60 z-[5] pointer-events-none">
                          <div className="w-6 h-6 border-2 border-foreground !border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleFileRemove(index)}
                        aria-label="Remove file"
                        className="absolute top-0 right-0 w-[18px] h-[18px] flex items-center justify-center rounded-bl-[15px] bg-black/70 text-white text-[11px] leading-none hover:bg-black outline-none z-10"
                      >
                        ×
                      </button>
                      {isOverLimit && (
                        <Tooltip
                          title="You can attach up to 10 files per chat. Please remove files to allow this one."
                          placement="top"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: "hsl(var(--tooltip))",
                                color: "hsl(var(--tooltip-foreground))",
                                fontSize: "13px",
                                padding: "10px 12px",
                                borderRadius: "8px",
                                maxWidth: "280px",
                                textAlign: "center",
                              },
                            },
                            arrow: { sx: { color: "hsl(var(--tooltip))" } },
                          }}
                        >
                          <span className="absolute bottom-2 right-2 w-[22px] h-[22px] flex items-center justify-center  bg-card text-[#e69d17] cursor-help z-10">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-triangle-alert"
                              aria-hidden="true"
                            >
                              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
                              <path d="M12 9v4"></path>
                              <path d="M12 17h.01"></path>
                            </svg>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  );
                })}
              </div>
            )}


            <div
            >
              {/* The writing area carries the padding and the textarea sits at
                  p-0 inside it, so the 88px floor is writing space rather than
                  padding — this is what gives the composer its height. */}
              <div className="px-6 pt-5 pb-3">
              <textarea
                ref={textareaRef}
                className="v3chat__input bg-transparent !text-foreground text-sm font-light leading-relaxed placeholder:text-foreground/25 overflow-y-auto overflow-x-hidden resize-none min-h-[88px] max-h-[200px] w-full p-0 border-0 outline-none"
                placeholder={"Describe a Task and let's get to work"}
                value={newMessage}
                onChange={handleChange}
                onKeyDown={detectEnterPress}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                rows={1}
              // disabled={
              //   !isAuto && asistantSelected === undefined && !SSO?.openAgentBuilder
              // }
              />
              </div>

              {/* CU-86cb3nhmf: footer toolbar sits in its own band, divided from
                the writing area and running edge-to-edge under the card. */}
              <div className="flex justify-between items-center border-t border-border/60 bg-muted/40 px-6 py-2.5 rounded-b-3xl">
                <div className="flex gap-2 items-center">
                  <TooltipProvider delayDuration={150}>
                    <HoverTip>
                      <TooltipTrigger asChild>
                        <div className="hover:bg-muted rounded-lg">
                          <Uploadfile
                            ref={uploadFileRef}
                            classname="!outline-none shadow-none"
                            setSelectedFiles={setSelectedFiles}
                            chatId={params?.id}
                            onFileSelect={addFilesWithUpload}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">Attach file</TooltipContent>
                    </HoverTip>
                  </TooltipProvider>
                  {selectedFiles?.length > 0 && (
                    <button
                      className="relative text-[12px] text-muted-foreground outline-none"
                      onClick={() => setFilesPopupOpen((prev) => !prev)} // ⬅️ add this
                    >
                      {/* popup */}
                      <div
                        className={`absolute bottom-5 left-0 z-[9999] ${filesPopupOpen ? "block" : "hidden"}`}
                      >
                        {selectedFiles?.map((file, index) => {
                          const fileName = file.name.split("/").pop();
                          const isFirst = index === 0;
                          const isLast = index === selectedFiles.length - 1;

                          const borderRadiusStyle = {
                            borderRadius: `${isFirst ? "5px" : "0px"} ${isFirst ? "5px" : "0px"
                              } ${isLast ? "5px" : "0px"} ${isLast ? "5px" : "0px"}`,
                            width: "242px",
                          };

                          return (
                            <div
                              key={index}
                              className="px-6 py-2.5 h-[54px] relative flex items-center w-[90%] m-auto bg-popover "
                              style={borderRadiusStyle}
                            >
                              <div className="flex items-center">
                                <span>
                                  <Icon id="filePdf" />
                                </span>
                                <p className="p-[15px] m-0">
                                  {fileName.length > 10
                                    ? `${fileName.slice(0, 10)}...`
                                    : fileName}
                                </p>
                              </div>

                              <span
                                className="cursor-pointer ml-auto"
                                onClick={() => handleFileRemove(index)}
                              >
                                <Icon id="delete" />
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {selectedFiles.length} attached files{" "}
                      <Icon id="upArrow12" color="hsl(var(--muted-foreground))" />
                    </button>
                  )}
                </div>

                <div className="flex gap-2.5 items-center">
                  {recordingLoading ? (
                    <TooltipProvider delayDuration={150}>
                      <HoverTip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="Stop listening"
                            onClick={() => {
                              stopListening();
                            }}
                          >
                            <Icon id="chatContinuous" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Stop listening</TooltipContent>
                      </HoverTip>
                    </TooltipProvider>
                  ) : (
                    <div className="flex gap-2 items-center">
                      <TooltipProvider delayDuration={150}>
                        <HoverTip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label="Voice Input"
                              className="outline-none grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
                              onClick={() => {
                                startListening();
                              }}
                            >
                              <MicIcon size={20} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Voice Input</TooltipContent>
                        </HoverTip>
                      </TooltipProvider>

                      <TooltipProvider delayDuration={150}>
                        <HoverTip>
                          <TooltipTrigger asChild>
                            <button
                              aria-label="Send message"
                              onClick={() => {
                                if (!newMessage.trim()) return;
                                if (isUserChating) return;
                                submitNewMessage(
                                  newMessage,
                                  "savetodb",
                                  placeholdervalue ===
                                    "What can your AI help you with next?"
                                    ? "notimage"
                                    : "image",
                                  setNewMessage,
                                  resetTranscript(),
                                );
                              }}
                              disabled={!newMessage.trim()}
                              className={`outline-none grid h-11 w-11 place-items-center rounded-md border shadow-sm transition-[background-color,border-color] duration-200 ease-out ${newMessage.trim()
                                ? "border-foreground bg-foreground text-background hover:bg-foreground/90"
                                : "border-border bg-muted text-muted-foreground opacity-60 shadow-none cursor-not-allowed"
                                }`}
                            >
                              <SendArrowIcon size={18} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Let's go</TooltipContent>
                        </HoverTip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <CommonModal
              isOpen={OpenErrorModal}
              onClose={() => setOpenErrorModal(false)}
              title=""
              showAlert={false}
            >
              <p className="text-[#f00] text-[18px] font-bold">
                Your browser doesn’t support speech recognition.
              </p>
            </CommonModal>
          </div>
        </div>
        <CommonModal
          isOpen={OpenErrorModal}
          onClose={() => setOpenErrorModal(false)}
          title=""
          showAlert={false}
        >
          <p className="text-[#f00] text-[18px] font-bold">
            Your browser doesn’t support speech recognition.
          </p>
        </CommonModal>
      </div>
    </div >
  );
};

export default ChatInput;